import { useState, useEffect } from "react";
import { useWallet } from '../hooks/useWallet';
import AgentKeyModal from './AgentKeyModal';

export default function TradesList({
  portfolioId = 2,
  portfolioName = '',
  isTestnet = false,
  config = { apiBaseUrl: "http://localhost:8000" }, // config.apiBaseUrl default for local dev
  showEmpty = true
}) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showAgentKeyModal, setShowAgentKeyModal] = useState(false);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      
      // Get agent private key from localStorage
      let agentPrivateKey = null;
      const testnetKey = `hyperliquid_agent_testnet_${portfolioId}`;
      const mainnetKey = `hyperliquid_agent_mainnet_${portfolioId}`;
      
      // Try testnet key first, then mainnet
      agentPrivateKey = localStorage.getItem(testnetKey) || localStorage.getItem(mainnetKey);
      
      // Also try legacy format for backward compatibility
      if (!agentPrivateKey) {
        agentPrivateKey = localStorage.getItem(`agent_private_key_${portfolioId}`);
      }
      
      const url = `${config.apiBaseUrl}/portfolio/${portfolioId}/trade`;
      
      // SECURITY: Use X-Agent-Private-Key header instead of query param (not visible in logs/URLs)
      const headers = {
        'accept': 'application/json',
      };
      if (agentPrivateKey) {
        headers['X-Agent-Private-Key'] = agentPrivateKey;
      }
      
      const response = await fetch(url, {
        headers
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) errorMessage = errorData.detail;
        } catch (_) { /* ignore parse failure */ }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      // Sort trades by timestamp (newest first)
      const sortedTrades = Array.isArray(data) 
        ? data.sort((a, b) => b.time - a.time)
        : [];
      
      setTrades(sortedTrades);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch trades:', err);
      setError(err.message);
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [portfolioId]);

  const formatCurrency = (value, currency = 'USD') => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatAmount = (value, minimumFractionDigits = 2, maximumFractionDigits = 8) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getSideColor = (side) => {
    switch (side.toUpperCase()) {
      case 'A': // Ask/Sell
        return 'text-red-400 bg-red-400/10 border-red-400/20';
      case 'B': // Bid/Buy
        return 'text-green-400 bg-green-400/10 border-green-400/20';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const getSideLabel = (side) => {
    switch (side.toUpperCase()) {
      case 'A':
        return 'SELL';
      case 'B':
        return 'BUY';
      default:
        return side.toUpperCase();
    }
  };

  if (loading && trades.length === 0) {
    return (
      <div className="w-full p-px sm:p-2">
        <div className="bg-slate-800/50 border border-slate-700 rounded p-2 sm:p-6">
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-white">Loading trades...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const isAgentKeyError = error.includes('X-Agent-Private-Key header is required');
    return (
      <div className="w-full p-px sm:p-2">
        <div className="bg-red-900/20 border border-red-800 rounded p-2 sm:p-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-400 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-red-400 font-semibold mb-1">Failed to load trades</h3>
              {isAgentKeyError && (
                <p className="text-red-300 text-sm font-bold">Note: If you onboarded using OpenClaw, you may need to set your agent key. Ask your OpenClaw agent to provide it to you.</p>
              )}
              <p className="text-red-300 text-sm">{error}</p>
              <div className="flex gap-2 mt-3">
                {isAgentKeyError && (
                  <button
                    onClick={() => setShowAgentKeyModal(true)}
                    className="px-4 py-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded text-sm transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Set Agent Key
                  </button>
                )}
                <button
                  onClick={fetchTrades}
                  className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white rounded text-sm transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
        <AgentKeyModal
          isOpen={showAgentKeyModal}
          portfolioId={portfolioId}
          isTestnet={isTestnet}
          portfolioName={portfolioName}
          onSave={() => { setShowAgentKeyModal(false); fetchTrades(); }}
          onClose={() => setShowAgentKeyModal(false)}
        />
      </div>
    );
  }

  if (trades.length === 0 && !showEmpty) {
    return null;
  }

  if (trades.length === 0) {
    return (
      <div className="w-full p-px sm:p-2">
        <div className="bg-slate-800/50 border border-slate-700 rounded p-2 sm:p-6">
          <div className="text-center">
            <svg className="w-12 h-12 text-slate-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-slate-400 text-lg font-medium mb-2">No trades found</h3>
            <p className="text-slate-500 text-sm">
              No trading activity for portfolio {portfolioId}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-px sm:p-2">
      <div className="bg-slate-800/50 border border-slate-700 rounded overflow-hidden">
        {/* Header */}
        <div className="p-1 sm:p-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <h2 className="text-sm sm:text-lg font-semibold text-white">
              Trades
            </h2>
            <div className="flex items-center gap-1 sm:gap-4">
              <span className="text-xs sm:text-sm text-slate-400">
                {trades.length}
              </span>
              {lastUpdated && (
                <span className="text-xs text-slate-500 hidden sm:inline">
                  {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <div
            className={`${
              trades.length > 8 ? "max-h-96 overflow-y-auto" : ""
            }`}
          >
            <table className="w-full">
              <thead className="bg-slate-700 sticky top-0 z-10">
                <tr>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Coin
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Side
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider hidden sm:table-cell">
                    Size
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider hidden sm:table-cell">
                    Fee
                  </th>
                  <th className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider hidden md:table-cell">
                    Order ID
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {trades.map((trade) => (
                  <tr 
                    key={trade.hash} 
                    className="hover:bg-slate-700/20 transition-colors"
                  >
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-xs sm:text-sm text-slate-300">
                      {formatTime(trade.time)}
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3">
                      <div className="flex items-center">
                        <span className="text-xs sm:text-sm font-medium text-white">
                          {trade.coin}
                        </span>
                      </div>
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-center">
                      <span className={`inline-flex px-1 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full border ${getSideColor(trade.side)}`}>
                        {getSideLabel(trade.side)}
                      </span>
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs sm:text-sm text-slate-300">
                      {formatCurrency(trade.px)}
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs sm:text-sm text-slate-300 hidden sm:table-cell">
                      {formatAmount(trade.sz,2,6)}
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs sm:text-sm font-medium text-white">
                      {formatCurrency(trade.sz * trade.px)}
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs sm:text-sm text-slate-400 hidden sm:table-cell">
                      {formatCurrency(trade.fee)}
                    </td>
                    <td className="px-1 sm:px-4 py-1 sm:py-3 text-right text-xs sm:text-sm text-slate-500 hidden md:table-cell">
                      #{trade.oid}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        {trades.length > 0 && (
          <div className="p-4 border-t border-slate-700/50 bg-slate-700/10">
            <div className="flex justify-between items-center text-sm text-slate-400">
              <span>
                Showing {trades.length} trade{trades.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={fetchTrades}
                className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs transition-colors"
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
